import { useState, useEffect } from "react";
import { motion } from "framer-motion";

// UTM helper
const trackCTA = (action) => {
  const params = {
    utm_source: "landing_page",
    utm_medium: "cta_button",
    utm_campaign: "oet_launch_2026",
    utm_content: action,
    ts: Date.now(),
  };
  if (window.dataLayer) {
    window.dataLayer.push({ event: "cta_click", cta_type: action, ...params });
  }
  if (window.posthog) {
    window.posthog.capture("cta_click", { cta_type: action, ...params });
  }
};

const MAILTO_TARGET = "oet.founder@gmail.com";

const PITCH_DECK_SUBJECT = encodeURIComponent("Investor Inquiry — OASIS Ecosystem Technology");
const PITCH_DECK_BODY = encodeURIComponent(
  `Dear OASIS Team,\n\nI'm an investor and I'm interested in your startup. I'd like to learn more about OASIS Ecosystem Technology and request the Pitch Deck.\n\nPlease share more details about the project, current stage, and investment opportunity.\n\nBest regards`
);

const WAITLIST_SUBJECT = encodeURIComponent("Waitlist — OASIS Ecosystem Technology");
const WAITLIST_BODY = encodeURIComponent(
  `Dear OASIS Team,\n\nI'm interested in your project and would love to be among the first to experience it.\n\nPlease let me know when the first product is ready for launch.\n\nLooking forward to hearing from you!`
);

const HeroSection = () => {
  const handlePitchDeck = () => {
    trackCTA("request_pitch_deck");
    window.location.href = `mailto:${MAILTO_TARGET}?subject=${PITCH_DECK_SUBJECT}&body=${PITCH_DECK_BODY}`;
  };

  const handleWaitlist = () => {
    trackCTA("join_waitlist");
    window.location.href = `mailto:${MAILTO_TARGET}?subject=${WAITLIST_SUBJECT}&body=${WAITLIST_BODY}`;
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Ambient radial glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full opacity-[0.07] pointer-events-none"
        style={{ background: "radial-gradient(circle, #00f0ff 0%, transparent 70%)" }}
      />
      <div
        className="absolute top-[20%] right-[10%] w-[400px] h-[400px] rounded-full opacity-[0.04] pointer-events-none"
        style={{ background: "radial-gradient(circle, #7b61ff 0%, transparent 70%)" }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.1, ease: "easeOut" }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="metallic-text text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-tight"
          >
            Redefining silence as the ultimate luxury.
          </motion.h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-8 text-base lg:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed font-light"
        >
          Building the physical infrastructure for mental resilience. From Earth
          to Mars.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            data-testid="hero-pitchdeck-btn"
            onClick={handlePitchDeck}
            className="btn-cyan-glow px-8 py-3.5 rounded-full text-sm font-semibold tracking-wide cursor-pointer"
          >
            Request Pitch Deck
          </button>
          <button
            data-testid="hero-waitlist-btn"
            onClick={handleWaitlist}
            className="px-8 py-3.5 rounded-full text-sm font-semibold tracking-wide border border-white/15 text-zinc-300 hover:text-offWhite hover:border-white/30 transition-all duration-300 cursor-pointer"
          >
            Join the Waitlist
          </button>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-deepSpace to-transparent z-10" />
    </section>
  );
};

export default HeroSection;
